import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TNil } from '@app/app.interface';
import { BriefingNotesComponent } from '@campaign-form/form-fields/creators-and-ctas/briefing-notes/briefing-notes.component';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import {
  ICreatorCtaKpiStatusApi,
  ICtaResultApi,
  IPlannedExecutionApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IBriefingNotesDialogData } from '@shared/components/briefing-notes-dialog/briefing-notes-dialog.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { SocialContentTileComponent } from '@shared/components/social-content-tile/social-content-tile.component';
import { ITileConfiguration } from '@shared/components/social-content-tile/social-content-tile.interface';
import { SocialContentTileService } from '@shared/components/social-content-tile/social-content-tile.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { camelCase } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ICtaTemplateItem, INextUpGroup, IPlatformFeatureGroup } from './campaign-details-creator-overview.interface';

@Component({
  imports: [
    BriefingNotesComponent,
    MatButtonModule,
    NgxSkeletonLoaderModule,
    SocialBadgeComponent,
    SocialContentTileComponent,
    TranslateModule,
    IconComponent,
    NgTemplateOutlet,
  ],
  selector: 'app-campaign-details-creator-overview',
  templateUrl: './campaign-details-creator-overview.component.html',
})
export class CampaignDetailsCreatorOverviewComponent {
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly ctaKpiStatus = input.required<ICreatorCtaKpiStatusApi[] | null>();
  // no_expectations executions - never part of analyticsNodes, since nothing is owed for them.
  public readonly optionalAnalyticsNodes = input<IAnalyticsNode[] | null>(null);

  public readonly tileClicked = output<ITileConfiguration>();
  public readonly viewAllPlannedContentsClicked = output<void>();

  public readonly platformFeatureCardExpanded = signal(false);
  public readonly ctaTemplateCardExpanded = signal(false);

  private readonly tileService = inject(SocialContentTileService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);

  public readonly briefingNotesData = computed<IBriefingNotesDialogData>(() => {
    const campaign = this.campaign();
    const campaignCreator = campaign?.campaignCreators?.[0] ?? null;
    const creatorBriefingNote =
      campaign?.briefingNotes?.find((note) => note.creatorId === campaignCreator?.creatorId) ?? null;
    const globalBriefingNote = campaign?.briefingNotes?.find((note) => !note.creatorId) ?? null;
    const briefingNote = creatorBriefingNote ?? globalBriefingNote;

    return {
      briefingNote,
      campaignId: campaign?.id ?? '',
      creatorId: campaignCreator?.creatorId ?? null,
      creatorName: campaignCreator?.creator?.name ?? null,
      enableTextShowMore: true,
      hideMessageSubtitle: true,
      subtitleFontSize: 14,
      subtitleFontWeight: 700,
      viewMode: true,
    };
  });

  public readonly contentFormatsLoading = computed<boolean>(() => {
    if (!this.campaign()) {
      return true;
    }

    if (this.campaign()!.ctaTemplates?.length) {
      return false;
    }

    return !this.analyticsNodes();
  });

  public readonly alwaysTiles = computed<ITileConfiguration[]>(() => {
    const nodes = this.analyticsNodes() ?? [];

    return nodes
      .filter(
        (node) =>
          this.getPlannedExecution(node)?.executionType === EExecutionType.always &&
          node.executionStatus === EMatchingType.planned,
      )
      .map((node) => this.tileService.prepareTileData(node));
  });

  public readonly uniquePlannedTiles = computed<ITileConfiguration[]>(() => {
    const nodes = this.analyticsNodes() ?? [];

    return nodes
      .filter(
        (node) =>
          this.getPlannedExecution(node)?.executionType === EExecutionType.unique &&
          node.executionStatus === EMatchingType.planned,
      )
      .sort((a, b) => {
        if (!a.executionTime) {
          return 1;
        }

        if (!b.executionTime) {
          return -1;
        }

        return new Date(a.executionTime).getTime() - new Date(b.executionTime).getTime();
      })
      .map((node) => this.tileService.prepareTileData(node));
  });

  public readonly undesignatedTiles = computed<ITileConfiguration[]>(() => {
    const nodes = this.analyticsNodes() ?? [];
    const seen = new Set<string>();

    return nodes
      .filter((node) => {
        if (
          this.getPlannedExecution(node)?.executionType !== EExecutionType.undesignated ||
          node.executionStatus !== EMatchingType.planned
        ) {
          return false;
        }

        const key = node.nonExecutedCtaId ?? node.id;

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);

        return true;
      })
      .map((node) => {
        const plannedExecution = this.getPlannedExecution(node);

        return {
          ...this.tileService.prepareTileData(node),
          remainingExecutionCount:
            (plannedExecution?.executionCountTarget ?? 0) - (plannedExecution?.executionCountCurrent ?? 0),
        };
      });
  });

  public readonly timeFrameTiles = computed<ITileConfiguration[]>(() => {
    const nodes = this.analyticsNodes() ?? [];
    const seenCtaExecutionIds = new Set<string>();

    return nodes
      .filter((node) => {
        const plannedExecution = this.getPlannedExecution(node);

        if (
          plannedExecution?.executionType !== EExecutionType.timeFrame ||
          node.executionStatus !== EMatchingType.planned ||
          seenCtaExecutionIds.has(plannedExecution.ctaExecutionId)
        ) {
          return false;
        }

        seenCtaExecutionIds.add(plannedExecution.ctaExecutionId);

        return true;
      })
      .sort((a, b) => this.getPlannedEndTimestamp(a) - this.getPlannedEndTimestamp(b))
      .map((node) => {
        const plannedExecution = this.getPlannedExecution(node);

        return {
          ...this.tileService.prepareTileData(node),
          remainingExecutionCount:
            (plannedExecution?.executionCountTarget ?? 0) - (plannedExecution?.executionCountCurrent ?? 0),
        };
      });
  });

  public readonly optionalTiles = computed<ITileConfiguration[]>(() =>
    (this.optionalAnalyticsNodes() ?? []).map((node) => this.tileService.prepareTileData(node)),
  );

  public readonly platformFeatureGroups = computed<{ total: number; items: IPlatformFeatureGroup[] }>(() => {
    const nodes = this.analyticsNodes() ?? [];
    const countMap = new Map<EPlatformFeature, number>();
    const seenCtaExecutionIds = new Set<string>();

    nodes.forEach((node) => {
      const plannedExecution = this.getPlannedExecution(node);

      if (
        plannedExecution?.executionType === EExecutionType.always ||
        plannedExecution?.executionType === EExecutionType.unique
      ) {
        const prev = countMap.get(node.platformFeature) ?? 0;
        countMap.set(node.platformFeature, prev + 1);
      } else if (
        plannedExecution?.executionType === EExecutionType.undesignated ||
        plannedExecution?.executionType === EExecutionType.timeFrame
      ) {
        const ctaExecutionId = plannedExecution?.ctaExecutionId;

        if (ctaExecutionId && seenCtaExecutionIds.has(ctaExecutionId)) {
          return;
        }

        if (ctaExecutionId) {
          seenCtaExecutionIds.add(ctaExecutionId);
        }

        const target = plannedExecution?.executionCountTarget ? Number(plannedExecution?.executionCountTarget) : 1;
        const prev = countMap.get(node.platformFeature) ?? 0;
        countMap.set(node.platformFeature, prev + target);
      }
    });

    const items: IPlatformFeatureGroup[] = Array.from(countMap.entries())
      .map(([platformFeature, count]) => ({
        color: this.tileService.getPlatformFeatureSocialColor(platformFeature),
        count,
        platformFeature,
      }))
      .sort((a, b) => b.count - a.count);

    const total = items.reduce((sum, item) => sum + item.count, 0);

    return { items, total };
  });

  public readonly ctaTemplateItems = computed<{
    total: number;
    items: ICtaTemplateItem[];
  }>(() => {
    const templates = this.campaign()?.ctaTemplates ?? [];
    const nodes = this.analyticsNodes();

    if (!templates.length) {
      return { items: this.ctaTemplatesFromNodes() ?? [], total: this.ctaTemplatesFromNodes()?.length ?? 0 };
    }

    const activePlatformFeatures = nodes?.length ? new Set(nodes.map((n) => n.platformFeature)) : null;
    const filteredTemplates =
      activePlatformFeatures !== null
        ? templates.filter((t) => activePlatformFeatures.has(camelCase(t.platformFeature) as EPlatformFeature))
        : templates;

    return {
      items: filteredTemplates.map((t) => ({
        color: this.tileService.getPlatformFeatureSocialColor(t.platformFeature),
        name: t.name ?? '',
        platformFeature: t.platformFeature,
      })),
      total: filteredTemplates.length,
    };
  });

  public readonly campaignRequirements = computed<{ deliverables: number }>(() => {
    const nodes = this.analyticsNodes() ?? [];
    const countedExecutionIds = new Set<string>();
    let deliverables = 0;

    nodes.forEach((node) => {
      const bestCtaResult = this.tileService.pickMaxScoreCtaResult(
        node.content?.ctas ? Array.from(node.content?.ctas.values()) : [],
      );
      const ctaExecutionId = node.plannedExecution?.ctaExecutionId ?? bestCtaResult?.plannedExecution?.ctaExecutionId;

      if (ctaExecutionId && countedExecutionIds.has(ctaExecutionId)) {
        return;
      }

      if (ctaExecutionId) {
        countedExecutionIds.add(ctaExecutionId);
      }

      const plannedExecution = this.getPlannedExecution(node);

      // no_expectations owes nothing: its tracked content is all bonus.
      if (plannedExecution?.executionType === EExecutionType.noExpectations) {
        return;
      }

      const tile = this.tileService.prepareTileData(node);
      const multiplier =
        plannedExecution?.executionType === EExecutionType.undesignated ||
        plannedExecution?.executionType === EExecutionType.timeFrame
          ? (plannedExecution?.executionCountTarget ?? 1)
          : 1;

      deliverables += tile.deliverableTypeNames.length * multiplier;
    });

    return { deliverables };
  });

  public readonly progressData = computed<{ current: number; total: number }>(() => {
    if (this.ctaKpiStatus()?.length) {
      const status = this.ctaKpiStatus()![0].ctaKpiStatus;

      return { current: status.currentMatchedContents, total: status.expectedContents };
    }

    const nodes = this.analyticsNodes() ?? [];
    const total = nodes.filter(
      (n) => n.executionStatus !== EMatchingType.bonus && n.executionStatus !== EMatchingType.unrelated,
    ).length;
    const current = nodes.filter((n) => n.executionStatus === EMatchingType.matched).length;

    return { current, total };
  });

  public readonly nextUpGroups = computed<INextUpGroup[]>(() => {
    const tiles = this.uniquePlannedTiles();

    if (!tiles.length) {
      return [];
    }

    const today = this.toDateKey(new Date());
    const tomorrow = this.toDateKey(new Date(Date.now() + 86400000));

    const seenDates: string[] = [];

    for (const tile of tiles) {
      if (!tile.executionDateTime) continue;

      const dateKey = this.toDateKey(new Date(tile.executionDateTime));

      if (!seenDates.includes(dateKey)) {
        seenDates.push(dateKey);
      }

      if (seenDates.length === 2) {
        break;
      }
    }

    return seenDates.map((dateKey) => {
      let label: string;

      if (dateKey === today) {
        label = this.translate.instant('pages.campaignDetails.creatorOverview.today');
      } else if (dateKey === tomorrow) {
        label = this.translate.instant('pages.campaignDetails.creatorOverview.tomorrow');
      } else {
        const parts = dateKey.split('-');
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        label = new DatePipe('en').transform(d, 'MMMM d, y') ?? dateKey;
      }

      const groupTiles = tiles.filter((tile) => {
        if (!tile.executionDateTime) return false;

        return this.toDateKey(new Date(tile.executionDateTime)) === dateKey;
      });

      return { label, tiles: groupTiles };
    });
  });

  private readonly ctaTemplatesFromNodes = computed<ICtaTemplateItem[]>(() => {
    const nodes = this.analyticsNodes() ?? [];

    return this.getCtaTemplatesFromNodes(nodes);
  });

  private toDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private getPlannedExecution(node: IAnalyticsNode): IPlannedExecutionApi | null {
    return node.plannedExecution ?? this.getMaxScoreCtaResult(node)?.plannedExecution ?? null;
  }

  // API dates are naive UTC - new Date() would read them as local time, which can flip two deadlines
  // around a daylight-saving change. A missing deadline sorts first.
  private getPlannedEndTimestamp(node: IAnalyticsNode): number {
    const plannedEnd = this.getPlannedExecution(node)?.plannedEnd;

    return plannedEnd ? this.dateUtils.parseDateTimeFromApi(plannedEnd).getTime() : 0;
  }

  private getMaxScoreCtaResult(node: IAnalyticsNode): ICtaResultApi | TNil {
    return this.tileService.pickMaxScoreCtaResult(node.content?.ctas ? Array.from(node.content?.ctas.values()) : []);
  }

  private getCtaTemplatesFromNodes(nodes: IAnalyticsNode[]): ICtaTemplateItem[] {
    const templates: Map<string, ICtaTemplateItem> = new Map();

    nodes.forEach((node) => {
      const ctaResults = node.content?.ctas ? Array.from(node.content?.ctas.values()) : [];

      if (!ctaResults.length && node.nonExecutedCtaId) {
        if (templates.has(node.nonExecutedCtaId)) {
          return;
        }

        templates.set(node.nonExecutedCtaId, {
          color: this.tileService.getPlatformFeatureSocialColor(node.platformFeature),
          name: node.nonExecutedCtaName ?? '',
          platformFeature: node.platformFeature,
        });

        return;
      }

      ctaResults.forEach((ctaResult) => {
        if (templates.has(ctaResult.ctaId)) {
          return;
        }

        templates.set(ctaResult.ctaId, {
          color: this.tileService.getPlatformFeatureSocialColor(node.platformFeature),
          name: ctaResult.name,
          platformFeature: node.platformFeature,
        });
      });
    });

    return Array.from(templates.values());
  }
}
